import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@components/SeoHead';
import GameCard from '@components/GameCard';
import GameDetailView from '@components/GameDetailView';
import { getFeaturedGames, getGameBySlug, getHomePageContent, getLatestGames } from '@lib/content';
import { siteConfig, DOM_IDS } from '../config';
import { useGameRecommendations } from '@hooks/useGameRecommendations';
import { useGamePlayer } from '@hooks/useGamePlayer';
import { decodeHtml } from '@lib/seo';
import { calculateCategoryCounts, getGameCategories, getLocalizedGame } from '@lib/game-utils';
import { buildSingleGameHomeSchema, buildHomePageSchema } from '@lib/jsonld';
import categories from '@content/categories.json';
import { useLanguage } from '@lib/i18n';


const HomePage: React.FC = () => {
  // Single-game mode: render game detail on homepage
  if (siteConfig.siteMode === 'single-game') {
    const gameSlug = siteConfig.gameSlug;
    let game = gameSlug ? getGameBySlug(gameSlug) : null;

    // Fallback logic
    if (!game) {
      const homeContent = getHomePageContent();
      if (!homeContent) {
        // No home.json, use first available game
        const latestGames = getLatestGames(1);
        game = latestGames[0] || null;
      }
    }

    // If we have a game, render it as homepage
    if (game) {
      return <SingleGameHomePage game={game} />;
    }
  }

  // Category mode rendering
  return <CategoryHomePage />;
};

// Single Game Mode Component
const SingleGameHomePage: React.FC<{ game: any }> = ({ game }) => {
  const { language } = useLanguage();
  const localizedGame = getLocalizedGame(game, language);

  const {
    iframeRef,
    gamePlayerRef,
    showBackToGame,
    gameStarted,
    isLoading,
    isTheaterMode,
    handlePlayClick,
    handleIframeLoad,
    scrollToGame,
    toggleTheaterMode,
    showCoverOverlay,
    hasInitializedFullscreen,
    handleOverlayPlay,
  } = useGamePlayer({ gameSlug: game.slug });

  const { sidebarGames, moreGames } = useGameRecommendations(game);

  const openFullscreen = () => {
    const node = iframeRef.current;
    if (!node) return;
    const el = node as HTMLElement & {
      webkitRequestFullscreen?: () => void;
      msRequestFullscreen?: () => void;
    };
    if (node.requestFullscreen) {
      node.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  };

  const scrollToComments = (evt: React.MouseEvent) => {
    evt.preventDefault();
    const commentsSection = document.getElementById(DOM_IDS.COMMENTS_WIDGET);
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get primary category for breadcrumb
  const primaryCategory = game.categories?.[0];
  const currentCategory = primaryCategory ? categories.find(c => c.slug === primaryCategory) : null;

  // Get all game categories for tags (using utility function)
  const gameCategories = useMemo(() => getGameCategories(game, 5), [game]);

  // Calculate actual game counts for each category (memoized)
  const categoryCounts = useMemo(() => calculateCategoryCounts(gameCategories), [gameCategories]);

  const jsonLdSchemas = buildSingleGameHomeSchema(game, game.faqs);

  return (
    <>
      <SeoHead
        rawTitle="PokeRogue | No Account Needed!"
        description={localizedGame.description}
        image={game.thumbnail}
        jsonLd={jsonLdSchemas}
        pagePath="/"
        canonicalUrl={`${siteConfig.siteUrl}/`}
      />
      <GameDetailView
        game={game}
        slug={game.slug}
        gameStarted={gameStarted}
        isLoading={isLoading}
        showBackToGame={showBackToGame}
        sidebarGames={sidebarGames}
        moreGames={moreGames}
        iframeRef={iframeRef}
        gamePlayerRef={gamePlayerRef}
        onPlayClick={handlePlayClick}
        onIframeLoad={handleIframeLoad}
        onScrollToGame={scrollToGame}
        isHomePage={true}
        isTheaterMode={isTheaterMode}
        onTheaterModeToggle={toggleTheaterMode}
        // new overlay props
        showCoverOverlay={showCoverOverlay}
        hasInitializedFullscreen={hasInitializedFullscreen}
        onOverlayPlay={handleOverlayPlay}
      />
    </>
  );
};

// Category Mode Component
const CategoryHomePage: React.FC = () => {
  const homeContent = getHomePageContent();
  const featuredGames = getFeaturedGames();

  // If no home content, show basic featured games
  if (!homeContent) {
    return (
      <>
        <SeoHead
          title={siteConfig.siteName}
          description={siteConfig.siteDescription}
          pagePath="/"
        />
        <div className="container">
          <section className="page-section">
            <div className="section-header">
              <h1>Featured Games</h1>
              <p>Our hand-picked best games! Top-rated titles with excellent gameplay and entertainment value.</p>
            </div>
            <div className="grid">
              {featuredGames.map((game) => (
                <GameCard key={game.slug} game={game} />
              ))}
            </div>
          </section>
        </div>
      </>
    );
  }

  // Category mode with home.json content
  const homeSchema = buildHomePageSchema();

  return (
    <>
      <SeoHead
        title={homeContent.title}
        description={homeContent.metaDescription}
        pagePath="/"
        jsonLd={homeSchema}
      />
      <div className="container">
        {/* Short intro section */}
        <section className="page-section" style={{ textAlign: 'center', paddingBottom: '2rem' }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{siteConfig.siteName}</h1>
          <p style={{ fontSize: '1.2rem', color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            {homeContent.shortIntro}
          </p>
        </section>

        {/* Featured games section */}
        <section className="page-section">
          <div className="section-header">
            <h2>Featured Games</h2>
            <p>Our hand-picked best games! Top-rated titles with excellent gameplay and entertainment value.</p>
          </div>
          <div className="grid">
            {featuredGames.map((game) => (
              <GameCard key={game.slug} game={game} />
            ))}
          </div>
        </section>

        {/* SEO content section */}
        <section className="page-section info-the-game" style={{ paddingTop: '3rem' }}>
          <div
            className="seo-content"
            dangerouslySetInnerHTML={{ __html: decodeHtml(homeContent.seoContent) }}
            style={{
              maxWidth: '900px',
              margin: '0 auto',
              lineHeight: '1.8',
            }}
          />
        </section>
      </div>
    </>
  );
};

export default HomePage;
